import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Link,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useBrand } from "../../context/BrandContext";

export default function LegalDocumentLayout({ title, updatedAt, children }) {
  const brand = useBrand();
  const theme = useTheme();
  const logoSrc =
    theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light;

  return (
    <Box
      sx={{
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        px: 2,
        pt: { xs: 3, md: 4 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Box
        component={RouterLink}
        to="/login"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
          flexShrink: 0,
          textDecoration: "none",
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt={brand.name}
          sx={{ height: { xs: 48, sm: 60 }, width: "auto" }}
        />
      </Box>

      <Container maxWidth="md" sx={{ flexShrink: 0, px: { xs: 0, sm: 2 } }}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            textAlign: "left",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" fontWeight={800}>
              {title}
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              size="small"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
            >
              Voltar ao login
            </Button>
          </Stack>

          {updatedAt ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
              Última atualização: {updatedAt}
            </Typography>
          ) : null}

          <Box
            sx={{
              color: "text.primary",
              "& h2": {
                typography: "subtitle1",
                fontWeight: 700,
                mt: 3,
                mb: 1,
                color: "text.primary",
              },
              "& p, & li": {
                typography: "body2",
                lineHeight: 1.8,
                mb: 1.5,
                color: "text.secondary",
              },
              "& strong": { color: "text.primary", fontWeight: 600 },
              "& a": {
                color: "primary.main",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              },
              "& ul": { pl: 2.5, mb: 2, color: "text.secondary" },
            }}
          >
            {children}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Link
              component={RouterLink}
              to="/privacy"
              underline="hover"
              sx={{ fontWeight: 500, fontSize: "0.875rem" }}
            >
              Política de Privacidade
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              underline="hover"
              sx={{ fontWeight: 500, fontSize: "0.875rem" }}
            >
              Termos de Uso
            </Link>
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              sx={{ fontWeight: 500, fontSize: "0.875rem" }}
            >
              Entrar no XPro
            </Link>
          </Stack>
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          sx={{ mt: 3, opacity: 0.85 }}
        >
          © {new Date().getFullYear()} {brand.name} — Xvision Tecnologia
        </Typography>
      </Container>
    </Box>
  );
}
